import EmojiEventsIcon from '@material-ui/icons/EmojiEvents';
import { useMutation } from '@tanstack/react-query';
import dayjs from 'dayjs';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import CurrencyFormat from 'react-currency-format';
import { MdPallet } from 'react-icons/md';
import { Button, Divider, Modal, Table, Tooltip, Whisper } from 'rsuite';
import { BaseCell, Layout } from '../../../../../../../components';
import { AlertWithTimestamp } from '../../../../../../../components/AlertWithTimestamp';
import { Exception } from '../../../../../../../data';
import { StoreData, SuppliersModalRequest, SuppliersModalResponse, suppliersModalService } from '../../../../../../../services/suppliersModalService';
import type { SuppliersExhibitionType } from '../../../../../../../store/useSuppliersExhibitionStore';
import { extractApiErrorMessage } from '../../../../../../../utils/extractApiErrorMessage';
import { NegotiationDataItemProps } from '../../NegociacaoFornecedor.types';
import { CostNegotiationMode, DEFAULT_COST_NEGOTIATION_MODE } from '../../costNegotiation';
import { competitivenessIndicator, formatDecimal } from '../../utils';
import { BestBuyPriceIndicator } from '../BestBuyPriceIndicator';
import { MoneyPercentageIndicator } from '../MoneyPercentageIndicator';
import styles from './StoresModal.module.scss';

const { Column, HeaderCell, Cell, Pagination } = Table;

// Altura de cabecalho e de linha da tabela deste modal. Moram em constante porque tres lugares
// precisam do mesmo numero: a propria tabela, que as recebe como propriedade; a conta da altura
// total; e o piso de altura durante o carregamento. Enquanto eram literais repetidos, mudar um deles
// deixava os outros discordando sem que nada reclamasse.
export const STORES_TABLE_HEADER_HEIGHT = 46;
export const STORES_TABLE_ROW_HEIGHT = 62;

// Teto de linhas que a tabela mostra sem rolar. Acima disso ela para de crescer e o resto da lista
// vira rolagem. Comportamento que ja existia, preservado de proposito.
export const STORES_TABLE_MAX_VISIBLE_ROWS = 20;

// Quantas linhas de espaco o corpo da tabela precisa ter enquanto a lista de lojas esta sendo
// buscada.
export const STORES_TABLE_LOADING_MIN_ROWS = 3;

interface StoresTableHeightInput {
  itemCount: number;
  loading: boolean;
  // As tres medidas entram como parametro com valor padrao para que o teste possa provar que o piso
  // acompanha a altura de linha, em vez de repetir um numero decorado que passaria igual se o piso
  // fosse um minimo fixo desligado do resto.
  headerHeight?: number;
  rowHeight?: number;
  maxVisibleRows?: number;
}

export const calculateStoresTableHeight = ({
  itemCount,
  loading,
  headerHeight = STORES_TABLE_HEADER_HEIGHT,
  rowHeight = STORES_TABLE_ROW_HEIGHT,
  maxVisibleRows = STORES_TABLE_MAX_VISIBLE_ROWS,
}: StoresTableHeightInput) => {
  const heightFromData = headerHeight + Math.min(itemCount, maxVisibleRows) * rowHeight;

  if (!loading) {
    return heightFromData;
  }

  // O maior dos dois, e nao o piso puro: recarga com o dado anterior ainda na tela ja tem altura de
  // sobra, e o piso nao pode encolher uma tabela que estava maior — a lista pularia de tamanho
  // debaixo do dedo de quem esta lendo.
  return Math.max(heightFromData, headerHeight + STORES_TABLE_LOADING_MIN_ROWS * rowHeight);
};

interface StoresModalProps {
  open: boolean;
  onClose: () => void;
  selectedRowData: NegotiationDataItemProps | null;
  costNegotiationMode?: CostNegotiationMode;
  // Qual agrupamento a grade está exibindo. Chega como propriedade explícita, e de propósito não é
  // lido do estado global aqui nem deduzido da forma da linha: a visão é campo de contrato da
  // requisição, e deduzi-la pela presença ou ausência do objeto de rede seria uma heurística que
  // passa a decidir errado no dia em que o contrato de resposta mudar de forma. Propriedade
  // explícita também é o que torna este componente testável sem montar estado global.
  suppliersExhibitionType: SuppliersExhibitionType;
}

interface StoreRowData extends StoreData {
  id?: string;
  name?: string;
  isReference?: boolean;
  currentCost?: number;
  currentPrice?: number;
  competitorPrice?: number;
  marginPercentage?: number;
  competitorMarginPercentage?: number;
  idealPrice?: number;
  productsToBePricedId?: string;
  description?: string;
  productId?: string;
  storeName: string;
  productFamilyId?: string;
  competitivenessIndicator?: null | 'RULE_CPI_MAX' | 'RULE_CPI_MIN' | 'RULE_CPI_AVG';
  newPrice?: number | null;
}

export const StoresModal: React.FC<StoresModalProps> = ({
  open,
  onClose,
  selectedRowData,
  costNegotiationMode = DEFAULT_COST_NEGOTIATION_MODE,
  suppliersExhibitionType,
}) => {
  const isNetworkExhibition = suppliersExhibitionType === 'PRODUCT_SUPPLIERS_NETWORK';

  const [pagination, setPagination] = useState({
    page: 0,
    size: 20,
  });
  const [sortColumn, setSortColumn] = useState<string>('');
  const [sortType, setSortType] = useState<'asc' | 'desc'>('asc');
  const [apiResponse, setApiResponse] = useState<SuppliersModalResponse | null>(null);

  const { mutate: fetchStoresData, isLoading: loading } = useMutation({
    mutationFn: (request: SuppliersModalRequest) => suppliersModalService.getStoresData(request),
    onSuccess: data => {
      setApiResponse(data);
    },
    onError: error => {
      const errorMessage = extractApiErrorMessage(error);

      if (!errorMessage) {
        const { message } = new Exception('Erro ao buscar dados!');
        AlertWithTimestamp({ message });
      } else {
        AlertWithTimestamp({ message: errorMessage });
      }
    },
  });

  useEffect(() => {
    if (open && selectedRowData) {
      const request: SuppliersModalRequest = {
        // Página, tamanho e ordenação não entram no corpo: o serviço os separa e envia como parâmetros
        // da cadeia de consulta da URL, que é onde os dois servidores do caminho os leem.
        page: pagination.page,
        size: pagination.size,
        sort: sortColumn ? [`${sortColumn},${sortType}`] : [],
        productId: selectedRowData?.productId,
        suppliersExhibitionType,
        // A chave de cluster só entra fora do grão rede. Ela não é enviada com valor indefinido: é
        // omitida. No grão rede a linha da grade é o produto e não existe cluster a informar, e uma
        // chave de cluster nula chegando ao serviço de fornecedores recorta a busca por um cluster
        // que não existe, o que devolve página vazia sem erro nenhum — embaixo de um contador
        // dizendo que existem N lojas.
        ...(isNetworkExhibition ? {} : { clusterId: selectedRowData?.cluster?.clusterId }),
      };

      fetchStoresData(request);
    }
  }, [open, pagination.page, pagination.size, sortColumn, sortType, selectedRowData, fetchStoresData, suppliersExhibitionType, isNetworkExhibition]);

  const storesData = useMemo(() => {
    if (!apiResponse?.content || !selectedRowData) return [];

    return apiResponse.content.map((store: StoreData, index: number) => ({
      ...store,
      id: store.storeId,
      name: store.storeName,
      isReference: index === 0,
      currentCost: store.cost,
      currentPrice: store.retailPrice,
      competitorPrice: store.competitorsPrice,
      marginPercentage: store.marginRetailPrice,
      competitorMarginPercentage: store.marginCompetitorsPrice,
      stock: store.stock
        ? {
            ...store.stock,
            date: store.stock.lastUpdated,
          }
        : undefined,
      productId: selectedRowData.productId,
      expensesPercent: store.expensesPercent,
      salesTaxPercentage: store.salesTaxPercentage,
      purchaseTaxPercentage: store.purchaseTaxPercentage,
      // Copiado sem recuo, de propósito. Este é o custo que o cálculo de preço ideal de compra usa
      // quando o contrato do cliente negocia em custo de entrada, e é o mesmo insumo que a linha da
      // grade usa para o mesmo produto. Trocar a ausência por zero ou pelo custo líquido aqui
      // apagaria a diferença entre "o cliente não negocia assim" e "o dado não chegou", que é
      // justamente a diferença que a célula precisa enxergar.
      entryCost: store.entryCost,
      newObjectiveMargin: store.newObjectiveMargin,
      // O recuo para o custo atual vale só quando o novo custo está de fato ausente. Um novo custo de
      // zero é valor legítimo digitado por gente, e trocá-lo pelo custo atual mostra ao comprador um
      // número que ninguém digitou — justamente o número que ele acabou de zerar.
      newCost: store.newCost ?? store.cost,
    }));
  }, [apiResponse, selectedRowData]);

  const handlePageChange = useCallback((page: number) => {
    setPagination(prev => ({
      ...prev,
      page: page - 1,
    }));
  }, []);

  const handleSortColumn = useCallback((dataKey: string, sortType: 'asc' | 'desc') => {
    setSortColumn(dataKey);
    setSortType(sortType);
    setPagination(prev => ({
      ...prev,
      page: 0,
    }));
  }, []);

  const tableHeight = calculateStoresTableHeight({ itemCount: storesData.length, loading });

  if (!open) {
    return null;
  }

  return (
    <Modal show={open} onHide={onClose} size="lg" className={styles.storesModal} backdrop="static">
      <Modal.Header>
        <Modal.Title className={styles['modal-title']}>Lojas da rede</Modal.Title>
        <div className={styles['header-container']}>
          <div className={styles['header-container__row']}>
            <div className={styles['header-container__content']}>
              <p>PRODUTO</p>
              <span>
                {selectedRowData?.productId ?? '-'} - <span className={styles['info']}>{selectedRowData?.description ?? '-'}</span>
              </span>
            </div>
            <div className={styles['header-container__content']}></div>
            <div className={styles['header-container__content']}>
              <p>FAMÍLIA</p>
              <span>{selectedRowData?.productFamilyId ?? '-'}</span>
            </div>
          </div>
          <div className={styles['header-container__row']}>
            {isNetworkExhibition ? (
              // No grão rede a linha da grade é o produto e não existe cluster a nomear, então o
              // campo dá lugar a uma caixa de conteúdo vazia, idêntica à que a primeira linha já
              // usa. Ela não é removida e não vira tracinho: tracinho sugeriria dado faltando quando
              // na verdade é campo sem sentido neste grão, e remover a caixa faria as duas restantes
              // ocuparem metade da largura cada e desalinharem em relação à linha de cima, porque
              // cada caixa recebe fator de crescimento um na folha de estilo.
              <div className={styles['header-container__content']}></div>
            ) : (
              <div className={styles['header-container__content']}>
                <p>CLUSTER</p>
                <span>{selectedRowData?.cluster?.clusterName ?? '-'}</span>
              </div>
            )}
            <div className={styles['header-container__content']}>
              <p>TOTAL DE LOJAS</p>
              {/* O cabeçalho lê tudo da linha da grade que foi clicada, e nunca da resposta da
                  chamada do modal. No grão rede o contador mora no objeto de rede; nas demais
                  visões, no objeto de cluster. O valor de reserva é o mesmo de sempre. */}
              <span>{(isNetworkExhibition ? selectedRowData?.network?.storeCount : selectedRowData?.cluster?.clusterItemsCount) ?? '-'}</span>
            </div>
            <div className={styles['header-container__content']}>
              <p>MARGEM OBJETIVA</p>
              {/* Margem nula sai como "-%", que se lê mal. É defeito de texto pré-existente, já
                  acontece hoje na visão cluster, e foi visto e deixado de propósito: corrigi-lo no
                  meio desta mudança misturaria duas intenções e tiraria de quem quiser medir o efeito
                  da visão rede a atribuição do que mudou. */}
              <span>{selectedRowData?.newObjectiveMargin ?? '-'}%</span>
            </div>
          </div>
        </div>
      </Modal.Header>

      <Modal.Body className={styles.modalBody}>
        <Layout.Section padded className={styles['layout-section']}>
          <Table
            id="stores-modal-table"
            data={storesData}
            loading={loading}
            headerHeight={STORES_TABLE_HEADER_HEIGHT}
            rowHeight={STORES_TABLE_ROW_HEIGHT}
            height={tableHeight}
            className="ipa-table"
            sortColumn={sortColumn}
            sortType={sortType}
            onSortColumn={handleSortColumn}
          >
            {/* Cada uma das seis colunas declara na CÉLULA o campo pelo qual o servidor a ordena.
                Na célula, e não na coluna: é de `cell.props.dataKey` que a tabela tira a chave que
                entrega ao manipulador de ordenação. Enquanto as células não declaravam nada, os seis
                cabeçalhos se anunciavam ordenáveis, o manipulador recebia chave indefinida e a
                requisição saía sem instrução de ordenação alguma — clicar não fazia efeito nenhum.

                Os nomes vêm do vocabulário que o serviço de fornecedores lê para a busca no grão
                loja, que é a busca que este modal usa. Não são escolhidos aqui, e não podem ser
                aproximados: nome fora do vocabulário não é recusado, o serviço recua em silêncio
                para ordenação por identificador de produto, e o comprador vê a lista não mudar sem
                nada que explique. */}
            {/* @ts-ignore */}
            <Column sortable flexGrow={1}>
              <HeaderCell>Loja</HeaderCell>
              <Cell dataKey="store_id">
                {(rowData: StoreRowData) => (
                  <BaseCell>
                    <BaseCell.MainContainer>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ fontWeight: 600 }}>{rowData.id?.length === 1 ? `0${rowData.id}` : rowData.id}</span>
                      </div>
                    </BaseCell.MainContainer>
                    <BaseCell.SubContainer>
                      <span>{rowData.name?.toUpperCase()}</span>
                    </BaseCell.SubContainer>
                  </BaseCell>
                )}
              </Cell>
            </Column>

            {/* @ts-ignore */}
            <Column sortable width={150}>
              <HeaderCell className={styles['gerenciador-page__table__header-cell']}>
                <div>
                  <p>Estoque e</p>
                  <p>venda diária</p>
                </div>
              </HeaderCell>
              <Cell dataKey="stock_quantity">
                {(rowData: StoreRowData) => (
                  <div className={styles['cost-indicator-cell']}>
                    {!rowData.stock ? (
                      <span className={styles['placeholder']}>--</span>
                    ) : (
                      <div className={styles['estoque-venda-container']}>
                        <div className={styles['estoque-venda-top']}>
                          <span className={styles['estoque-venda-icon']}>
                            <MdPallet width={10} height={10} />
                          </span>
                          <Whisper placement="top" speaker={<Tooltip>Estoque</Tooltip>}>
                            <span className={styles['estoque-venda-numero']}>{rowData.stock.quantity}</span>
                          </Whisper>
                        </div>
                        <div className={styles['estoque-venda-bottom']}>
                          <Whisper placement="left" speaker={<Tooltip>Dias de estoque</Tooltip>}>
                            <span className={styles['estoque-venda-badge']}>
                              {rowData.stock.coverageDays ? `${Math.round(rowData.stock.coverageDays)}d` : '-'}
                            </span>
                          </Whisper>
                          <Whisper placement="right" speaker={<Tooltip>Data última entrada do produto</Tooltip>}>
                            <span className={styles['estoque-venda-data']}>
                              {rowData.stock.lastUpdated && dayjs(rowData.stock.lastUpdated).isValid()
                                ? dayjs(rowData.stock.lastUpdated).format('DD/MM/YYYY')
                                : '--'}
                            </span>
                          </Whisper>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </Cell>
            </Column>

            {/* @ts-ignore */}
            <Column sortable width={110}>
              <HeaderCell className={styles['gerenciador-page__table__header-cell']}>
                <div>
                  <p>Custo atual</p>
                  <p></p>
                </div>
              </HeaderCell>
              <Cell dataKey="cost">
                {(rowData: StoreRowData) => (
                  <BaseCell>
                    <BaseCell.MainContainer>
                      {/* O formatador de moeda trata `undefined`, e não `null`: com null ele estoura a
                          tela inteira. Custo e PMZ vêm nulos quando a carga do dia não trouxe custo. */}
                      {rowData?.currentCost == null ? (
                        '--'
                      ) : (
                        <CurrencyFormat
                          fixedDecimalScale
                          decimalScale={2}
                          value={rowData.currentCost}
                          displayType="text"
                          prefix="R$ "
                          decimalSeparator=","
                          thousandSeparator="."
                        />
                      )}
                    </BaseCell.MainContainer>
                    <BaseCell.SubContainer>
                      <span>PMZ </span>
                      {rowData?.pmz == null ? (
                        '--'
                      ) : (
                        <CurrencyFormat
                          fixedDecimalScale
                          decimalScale={2}
                          value={rowData.pmz}
                          displayType="text"
                          prefix="R$ "
                          decimalSeparator=","
                          thousandSeparator="."
                        />
                      )}
                    </BaseCell.SubContainer>
                  </BaseCell>
                )}
              </Cell>
            </Column>

            {/* @ts-ignore */}
            <Column sortable width={130}>
              <HeaderCell className={styles['gerenciador-page__table__header-cell']}>
                <div>
                  <p>Preço</p>
                  <p>vigente</p>
                </div>
              </HeaderCell>
              <Cell dataKey="price_retail">
                {(rowData: StoreRowData) => (
                  <div className={styles['preco-vigente-cell-wrapper']}>
                    <p className={styles['cell-main-text']}>R$ {rowData?.currentPrice === null ? '--' : formatDecimal(rowData?.currentPrice || 0, 2)}</p>

                    <div className={styles['preco-vigente-sub-wrapper']}>
                      <MoneyPercentageIndicator
                        value={rowData?.marginPercentage}
                        newObjectiveMargin={rowData?.newObjectiveMargin}
                        tooltip="Margem do preço vigente"
                      />
                      <Divider vertical />
                      <Whisper placement="auto" trigger="hover" speaker={<Tooltip>Competitividade do preço vigente</Tooltip>}>
                        <EmojiEventsIcon className={[styles['trophy-icon'], 'margin-right'].join(' ')} />
                      </Whisper>
                      {rowData?.competitorPrice !== null ? (
                        <CurrencyFormat
                          decimalScale={1}
                          displayType="text"
                          className={styles['cell-subtext']}
                          value={((rowData?.currentPrice || 0) / (rowData?.competitorPrice || 0)) * 100}
                          suffix="%"
                        />
                      ) : (
                        <span className={styles['cell-subtext']}>--</span>
                      )}
                    </div>
                  </div>
                )}
              </Cell>
            </Column>

            {/* @ts-ignore */}
            <Column sortable width={130}>
              <HeaderCell className={styles['gerenciador-page__table__header-cell']}>
                <div>
                  <p>Preço</p>
                  <p>concorrência</p>
                </div>
              </HeaderCell>
              <Cell dataKey="competitors_price">
                {(rowData: StoreRowData) => (
                  <div className={styles['preco-concorrencia-cell-wrapper']}>
                    <div className={styles.flex}>
                      <p className={styles['cell-main-text']}>
                        R$ {rowData?.competitorPrice !== null ? formatDecimal(rowData?.competitorPrice || 0, 2) : '--'}
                      </p>
                      <span className={styles['left-icon']}>{competitivenessIndicator('RULE_CPI_MIN')}</span>
                    </div>

                    <div className={styles['preco-concorrencia-sub-wrapper']}>
                      <MoneyPercentageIndicator
                        value={rowData?.competitorMarginPercentage}
                        newObjectiveMargin={rowData?.newObjectiveMargin}
                        tooltip="Margem do preço concorrente"
                      />
                    </div>
                  </div>
                )}
              </Cell>
            </Column>

            {/* @ts-ignore */}
            <Column sortable width={130}>
              <HeaderCell className={styles['gerenciador-page__table__header-cell']}>
                <div>
                  <p>Preço ideal</p>
                  <p>de compra</p>
                </div>
              </HeaderCell>
              <Cell dataKey="preco_ideal_compra">
                {(rowData: StoreRowData) => {
                  // Quando o contrato do cliente negocia em custo de entrada e a loja não trouxe
                  // custo de entrada, a célula diz que não tem dado em vez de mostrar número.
                  const entryCostRuleHasNoEntryCost =
                    costNegotiationMode === CostNegotiationMode.ENTRY_COST && (rowData?.entryCost === null || rowData?.entryCost === undefined);

                  if (entryCostRuleHasNoEntryCost) {
                    return (
                      <div className={styles['cost-indicator-cell']}>
                        <span className={styles['placeholder']}>--</span>
                      </div>
                    );
                  }

                  const safeData = {
                    ...rowData,
                    expensesPercent: rowData?.expensesPercent,
                    salesTaxPercentage: rowData?.salesTaxPercentage,
                    purchaseTaxPercentage: rowData?.purchaseTaxPercentage,
                    // Nomeado aqui e não deixado por conta do espalhamento acima. O objeto sai desta
                    // função com a checagem de tipo anulada, então campo que falte nele não levanta
                    // erro de compilação nenhum: foi assim que este insumo ficou de fora sem que
                    // nada avisasse. O que está escrito é o que se consegue conferir.
                    entryCost: rowData?.entryCost,
                    newObjectiveMargin: rowData?.newObjectiveMargin,
                    objectiveMargin: rowData?.objectiveMargin,
                    marginRetailPrice: rowData?.marginRetailPrice,
                    competitorsPrice: rowData?.competitorsPrice || rowData?.retailPrice || 0,
                    retailPrice: rowData?.retailPrice || 0,
                    cost: rowData?.cost || 0,
                  } as any;

                  return <BestBuyPriceIndicator data={safeData} isSupplierModal={true} costNegotiationMode={costNegotiationMode} />;
                }}
              </Cell>
            </Column>
          </Table>

          <Pagination
            // O total é o que o servidor respondeu, e zero quando é zero. O recuo para vinte que
            // existia aqui fazia o paginador desenhar uma página cheia sobre uma tabela vazia.
            total={apiResponse?.totalElements ?? 0}
            activePage={pagination.page + 1}
            displayLength={pagination.size}
            lengthMenu={[
              { value: 20, label: '20' },
              { value: 50, label: '50' },
              { value: 100, label: '100' },
            ]}
            onChangePage={handlePageChange}
            onChangeLength={(size: number) => {
              setPagination(prev => ({ ...prev, size, page: 0 }));
            }}
          />

          <div className={styles.footer}>
            <Button onClick={onClose} appearance="primary">
              FECHAR
            </Button>
          </div>
        </Layout.Section>
      </Modal.Body>
    </Modal>
  );
};
